import { useEffect, useState, useContext } from "react";
import { MessageContext, MessageType } from "../contexts/MessageContext";
import { handleMessageClose } from "../services/messageService";

// Map type to background color
const bgColorMap: Record<MessageType, string> = {
  [MessageType.Info]: "bg-white",
  [MessageType.Warning]: "bg-cyan-100",
  [MessageType.Error]: "bg-red-100",
};

const MessageModal = () => {
  const { state: message, dispatch } = useContext(MessageContext);
  const [visible, setVisible] = useState(false);

  // Show modal whenever there is a message
  useEffect(() => {
    setVisible(message !== null);
  }, [message]);

  // Close modal on Escape key
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setVisible(false);
        dispatch({ type: "CLEAR_MESSAGE" }); // clear message from context
        handleMessageClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [visible, dispatch]);

  if (!visible || !message) return null;

  const bgColor = bgColorMap[message.type];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={message.type + " message"}
      className="overflow-y-auto overflow-x-hidden fixed inset-0 z-50 w-full h-full bg-white/[0.75] flex items-center justify-center"
    >
      <div className={`relative ${bgColor} rounded shadow border border-slate-300 max-w-fit w-full text-right`}>
        <button
          type="button"
          className="m-[5px] text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm inline-flex items-center"
          onClick={() => {
            setVisible(false);
            dispatch({ type: "CLEAR_MESSAGE" });
            handleMessageClose();
          }}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <div className="flex">
          <div className="px-8 pb-5 pt-1.5 text-slate-800 text-lg text-left leading-relaxed whitespace-pre-wrap">
            {message.content}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageModal;
